/**
 * IP 检测服务
 * 用于检测用户地理位置并自动设置语言
 */
// @ts-ignore
import { countries } from 'countries-list';

export interface LocationInfo {
  country: string;
  continent: string;
  isChina: boolean;
  ip?: string;
  countryCode?: string;
  region?: string;
}

export class IPDetectionService {
  private static instance: IPDetectionService;
  private locationCache: LocationInfo | null = null;
  
  private constructor() {}
  
  public static getInstance(): IPDetectionService {
    if (!IPDetectionService.instance) {
      IPDetectionService.instance = new IPDetectionService();
    }
    return IPDetectionService.instance;
  }

  public async getUserLocation(): Promise<LocationInfo | null> {
    // 如果已经有缓存的位置信息，直接返回
    if (this.locationCache) {
      return this.locationCache;
    }

    // 首先尝试从localStorage获取已保存的位置信息
    const savedLocationStr = localStorage.getItem('userLocation');
    if (savedLocationStr) {
      try {
        const savedLocation = JSON.parse(savedLocationStr);
        this.locationCache = savedLocation;
        console.log('📍 使用缓存的位置信息:', savedLocation);
        return savedLocation;
      } catch (error) {
        console.error('解析保存的位置信息失败:', error);
        localStorage.removeItem('userLocation');
      }
    }

    // 如果没有保存的信息，则获取IP地址
    try {
      const apiToken = import.meta.env.VITE_IP_API_TOKEN;
      const apiUrl = import.meta.env.VITE_CUSTOM_IP_API_URL || `https://ipinfo.io/json?token=${apiToken}`;
      
      console.log('🌐 正在获取真实IP位置信息...');
      const response = await fetch(apiUrl);
      const data = await response.json();
      
      if (data && data.country) {
        // 根据国家代码确定大洲
        const countryCode = data.country as keyof typeof countries;
        const country = countries[countryCode];
        
        if (country) {
          let continent = '';
          
          // 根据国家确定大洲
          switch (country.continent) {
            case 'AF': continent = 'Africa'; break;
            case 'AS': continent = 'Asia'; break;
            case 'EU': continent = 'Europe'; break;
            case 'NA': continent = 'North America'; break;
            case 'SA': continent = 'South America'; break;
            case 'OC': continent = 'Oceania'; break;
            default: continent = 'Asia';
          }
          
          const locationInfo: LocationInfo = {
            country: countryCode,
            continent,
            isChina: countryCode === 'CN' || countryCode === 'HK' || countryCode === 'MO' || countryCode === 'TW',
            ip: data.ip || '127.0.0.1',
            countryCode: countryCode,
            region: data.region || data.city || ''
          };
          
          console.log('🎯 检测到的位置信息:', locationInfo);
          
          // 保存到localStorage和缓存
          localStorage.setItem('userLocation', JSON.stringify(locationInfo));
          this.locationCache = locationInfo;
          
          return locationInfo;
        }
      }
    } catch (error) {
      console.error('获取用户位置信息失败:', error);
    }
    
    // 如果获取失败，返回null让调用方使用默认设置
    console.log('⚠️ 无法获取位置信息');
    return null;
  }

  /**
   * 判断用户是否在中国大陆/港澳台地区
   */
  public async isChineseUser(): Promise<boolean> {
    const location = await this.getUserLocation();
    return location?.isChina || false;
  }

  /**
   * 根据地理位置获取推荐语言
   * 中国大陆/港澳台 -> 中文
   * 其他地区 -> 英文
   */
  public async getRecommendedLanguage(): Promise<'zh' | 'en'> {
    const isChina = await this.isChineseUser();
    return isChina ? 'zh' : 'en';
  }

  /**
   * 根据地理位置获取推荐租户
   * 中国大陆 -> cn
   * 其他地区 -> intl
   */
  public async getRecommendedTenant(): Promise<'cn' | 'intl'> {
    const location = await this.getUserLocation();
    if (location?.countryCode === 'CN') {
      return 'cn';
    }
    return 'intl';
  }

  public clearCache(): void {
    this.locationCache = null;
    localStorage.removeItem('userLocation');
  }
}

export const ipDetectionService = IPDetectionService.getInstance();
